/**
 * ChatMessage Model
 * 
 * Defines the structure for persisted and UI-only chat messages.
 * The active new-chat request path sends conversation messages plus durable ask_user_input tool responses.
 * 
 * Key Design Decisions:
 * - Core fields (id, role, content, timestamp) are always present
 * - UI-only SSE progress messages are retained locally and excluded from server requests
 * - ask_user_input is client-owned and persists as assistant tool-call messages plus tool responses
 * - Legacy tool fields remain optional so older persisted sessions can still be loaded safely
 * 
 * Role Types:
 * - 'user': Messages from the user
 * - 'assistant': Messages from the AI assistant
 * - 'sse_event': UI-only progress messages from server-sent events
 *
 * Recent changes:
 * - Adds UI-only client action records that never enter durable model history.
 * - Align human-input answers and cancellations with the llm-runtime 0.7 contract.
 * - Mark stopped assistant output as UI-only interrupted content.
 * - Treat ask_user_input prompts as client-owned assistant tool calls.
 */

import type { ChatPlanSnapshot } from './ChatPlan';

export const HUMAN_INPUT_FREEFORM_MAX_LENGTH = 4000;

export interface HumanInputOption {
  id: string;
  label: string;
  description?: string;
}

export interface HumanInputQuestion {
  header: string;
  id: string;
  question: string;
  allowOther?: boolean;
  options: HumanInputOption[];
}

export interface HumanInputRequest {
  type?: 'single-select' | 'multiple-select';
  allowSkip?: boolean;
  questions: HumanInputQuestion[];
}

export interface PendingHumanInput {
  toolCallId: string;
  toolName: string;
  request: HumanInputRequest;
}

export interface HumanInputAnsweredOutcome {
  status: 'answered';
  answers: Record<string, string | string[]>;
}

export interface HumanInputCancelledOutcome {
  status: 'cancelled';
  reason: 'rejected' | 'skipped' | 'dismissed' | 'timeout';
  message?: string;
}

export type HumanInputAnswer = HumanInputAnsweredOutcome | HumanInputCancelledOutcome;

export interface ChatMessage {
  id: number;
  messageId?: number; // Database message sequence identifier for persistence
  role: 'user' | 'assistant' | 'function' | 'tool' | 'sse_event';
  content: string;
  timestamp: string;
  name?: string;  // Function name for function messages (required by OpenAI API)
  tool_call_id?: string;  // Tool call ID for tool responses (OpenAI SDK requirement)
  tool_calls?: Array<{  // Tool calls from assistant (OpenAI SDK format)
    id: string;
    type: 'function';
    function: {
      name: string;
      arguments: string;
    };
  }>;
  execution_source?: 'server' | 'client';  // Track where the function was executed
  function?: {           // Compatibility for older persisted function-call messages
    functionArgs?: any;   // Function call arguments (from tool_calls)
    functionResult?: any; // Function call results (from tool responses)
    status?: 'pending' | 'success' | 'error';  // Inferred status
  };

  // UI-only message support for SSE events
  isUIOnly?: boolean;    // Excludes from server conversation, display only
  isStopped?: boolean;   // Marks partial assistant output intentionally stopped by the user
  sseEventType?: 'tool_call' | 'progress' | 'human_input' | 'client_action' | 'plan';
  plan?: ChatPlanSnapshot;
  eventData?: SSEEventData;
}

export interface SSEEventData {
  eventType: string;
  status: string;
  message?: string;
  progress?: number;
  details?: any;
}

export class ChatServiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChatServiceError';
  }
}
